import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import { Group, PaymentMethod } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import De from "assets/libs/moment/src/locale/de";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

interface ExportRow {
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<PaymentMethod>();

const LIST_TEXT_MAX_LEN = 40;

function truncateListText(text: string, maxLen = LIST_TEXT_MAX_LEN): { display: string; full: string } {
  const full = (text || "").trim();
  if (!full) return { display: "", full: "" };
  if (full.length <= maxLen) return { display: full, full };
  return { display: `${full.slice(0, maxLen)}…`, full };
}

const SignagePaymentMethods = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const token = getAuthCookie("sessionToken");

  const [toastShown, setToastShown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.payment_methods
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [filteredTeammembers, setFilteredTeammembers] = useState<PaymentMethod[]>([]);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payment-methods/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {
                status: "",
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
              fields: ["id", "name", "description", "group_ids", "is_credit", "status", "created_at"],
            }),
          }
        );

        if (!response.ok) throw new Error("Failed to fetch payment methods");

        const result = await response.json();
        const methods = result.data ?? []; // Ensure this matches your backend response structure
        setPaymentMethod(methods);
        console.log("PaymentMethod:", methods);
        setRowCount(result?.total || 0);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, [token, pagination, globalFilter]);


  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/customer-groups`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        if (!response.ok) {
          showError(data.error || "Failed to fetch groups");
          throw new Error("Failed to fetch groups.");
        }
        // console.log("groups :", data);
        setGroups(data);
      } catch (err) {
        showError("Failed to load groups.");
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [token]);

  const handleDeleteClick = (method: PaymentMethod) => {
    setDeleteTarget({ id: method.id, name: method.name || "Payment Method" });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleDelete = async (paymentMethodId: string) => {
    setDeleting(paymentMethodId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment-methods/${paymentMethodId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        showError("Failed to delete the Payment Method");
        throw new Error("Failed to delete the Payment Method");
      }

      showSuccess("Payment Method deleted successfully!");
      setPaymentMethod((prev) => prev.filter((tm) => tm.id !== paymentMethodId));

      setDeleting(null); // Clear the deleting state

    } catch (err) {
      showError("Failed to delete the Payment Method");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null); // Clear the deleting state
    }
  };

  // if (loading) {
  //   return <div>Loading...</div>;
  // }

  const getGroupNames = (groupIds: Array<string | { id: string; name: string }> = []) => {
    if (!groupIds || groupIds.length === 0) return "";
    
    // Check if groupIds contains objects with name property (new format)
    if (groupIds.length > 0 && typeof groupIds[0] === "object" && groupIds[0] !== null && "name" in groupIds[0]) {
      return (groupIds as Array<{ id: string; name: string }>)
        .map((group) => group.name)
        .join(", ");
    }
    
    // Fallback to old format (array of string IDs)
    return (groupIds as string[])
      .map((id) => {
        const group = groups?.find((g) => g._id === id);
        return group ? group.name : id;
      })
      .join(", ");
  };

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text text-center">S.No.</div>,
        cell: (info) => (
          <div className="text-sm text-center">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      columnHelper.accessor(
        (row: PaymentMethod) => row?.name,
        {
          id: "name",
          header: () => <div className="th-text text-center">Name</div>,
          cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: PaymentMethod) => row?.description,
        {
          id: "description",
          header: () => <div className="th-text text-center">Description</div>,
          cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: PaymentMethod) => getGroupNames(row.group_ids),
        {
          id: "Groups",
          header: () => <div className="th-text text-center">Groups</div>,
          cell: (info) => {
            const { display, full } = truncateListText(String(info.getValue() ?? ""));
            return (
              <div className="text-sm text-center" title={full || undefined}>
                {display || <span className="text-muted">—</span>}
              </div>
            );
          },
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor("status", {
        header: () => <div className="th-text text-center"> Status</div>,
        cell: (info) => {
          const value = info.getValue() as string | boolean;
          const isActive = value === true || value === "true";

          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="text-sm text-center">
              <input
                type="radio"
                className={className}
                checked={isActive}
                readOnly
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text text-center">Actions</div>,
        cell: (info) => {
          const method = info.row.original;
          console.log("method", method);
          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              {canEdit && (
              <EditIconButton
                to={`${baseRoute}/editPaymentMethod/${method.id}`}
                className="btn action-btn fs-2 text-center"
                title="Edit"
                state={{ type: "edit" }}
              />
              )}
              {/* <Link
                to={`/signage/editPaymentMethod/${method._id}`}
                className="border-0 p-0"
                title="Edit"
              >
                <i className="ri-pencil-fill fs-20 text-primary"></i>
              </Link> */}
              {canDelete && (
              <DeleteIconButton
                type="button"
                className="icon-square danger"
                title="Delete"
                onClick={() => handleDeleteClick(method)}
                disabled={deleting === method.id}
              />
              )}
            </div>
          );
        },
      }),
    ];
  }, [baseRoute, deleting, pagination, globalFilter, canEdit, canDelete]);


  const table = useReactTable({
    data: paymentMethod,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const safePageCount = Math.max(0, Math.floor(pageCount || 0));
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content container">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0 "></h4>
        {/* <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to="/signage/SignageAddEditpaymentMethod"
            state={{ type: "add" }} className="btn btn-primary  table-addbutton">
            + Add Payment Method
          </Link>
          )}
        </div> */}
      </div>
      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0">Payment Methods</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Name"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
              />
            </div>
            {canCreate && (
              <Link to={`${baseRoute}/addPaymentMethod`}
              state={{ type: "add" }} className="btn btn-primary  table-addbutton">
              + Add Payment Method
            </Link>
            )}
            {/* <ExportAsExcel
                          data={exportRows}
                          headers={[
                            "Company Name",
                            "First Name",
                            "Last Name",
                            "Phone",
                            "Email",
                            "License Status",
                            "Status",
                          ]}
                          fileName="BrokerageFirms"
                        >
                          {(props) => (
                            <button {...props} className="export-button">
                              <i className="ri-file-excel-2-line me-2" />
                              Export to Excel
                            </button>
                          )}
                        </ExportAsExcel> */}
          </div>
        </div>
      </div>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                  orders
                </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading payment methods…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading payment methods…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
          )}
        </div>

      </div>
      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Payment Method"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>

  );
};

export default SignagePaymentMethods;
